import { apiSlice } from '../apiSlice';
import { DeleteResponse } from '../schemas/apiResponse';
import { Customer } from '../schemas/customer';
import { PaginatedArgs, PaginatedResponse } from '../schemas/pagination';
import { filterParamsAdapter } from '../utils/filterParamsAdapter';
import { providesList } from '../utils/providesList';

type CustomerFilterParams = {
  id__in?: string[];
  short_code__in?: string[];
};

const CUSTOMERS_PATH = '/customers';

const customerEndpoints = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * @deprecated Use `getPaginatedCustomers` instead.
     */
    getCustomers: builder.query<
      Customer[],
      (CustomerFilterParams & PaginatedArgs) | void
    >({
      query: (params) => ({
        url: `/v2${CUSTOMERS_PATH}`,
        params: params ? filterParamsAdapter(params) : undefined,
      }),
      transformResponse: (res: PaginatedResponse<Customer>) => {
        res.items.sort((a, b) => a.name.localeCompare(b.name));
        return res.items;
      },
      providesTags: (result) => providesList(result, 'Customer'),
    }),
    getPaginatedCustomers: builder.query<
      PaginatedResponse<Customer>,
      (CustomerFilterParams & PaginatedArgs) | void
    >({
      query: (params) => ({
        url: `/v2${CUSTOMERS_PATH}`,
        params: params
          ? { ...filterParamsAdapter(params), size: 1000 }
          : { size: 1000 },
      }),
      transformResponse: (res: PaginatedResponse<Customer>) => {
        res.items.sort((a, b) => a.name.localeCompare(b.name));
        return res;
      },
      providesTags: (result) => providesList(result?.items, 'Customer'),
    }),
    getCustomer: builder.query<Customer, string>({
      query: (id) => `${CUSTOMERS_PATH}/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Customer', id }],
    }),
    addCustomer: builder.mutation<Customer, Partial<Customer>>({
      query: (body) => ({
        url: CUSTOMERS_PATH,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Customer', id: 'LIST' }],
    }),
    updateCustomer: builder.mutation<Customer, Partial<Customer>>({
      query: (customer) => ({
        url: `${CUSTOMERS_PATH}/${customer.id}`,
        method: 'PATCH',
        body: customer,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Customer', id }],
    }),
    deleteCustomer: builder.mutation<DeleteResponse, string>({
      query(id) {
        return {
          url: `${CUSTOMERS_PATH}/${id}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (_result, _error, id) => [{ type: 'Customer', id }],
    }),
  }),
});

export const {
  useGetPaginatedCustomersQuery,
  useGetCustomerQuery,
  useAddCustomerMutation,
  useUpdateCustomerMutation,
  useDeleteCustomerMutation,
} = customerEndpoints;

/**
 * @deprecated Use `useGetPaginatedCustomersQuery` instead.
 */
export const useGetCustomersQuery = customerEndpoints.useGetCustomersQuery;
